// Agent teams (Schedule Hub's own org chart -- Adam Gragg's, Nick Boisseau's
// and Patrick Lacroix's reports, plus the Senior CSR and Management groups),
// used by the admin Reporting "AHT by account per rep" team filter and the
// Scorecards tab's roster + team filter. Moved here from
// ui/src/constants/teams.js (2026-10-08) so the SERVER can also use it as a
// Scorecards roster source (see admin-reporting.js's scorecards route) --
// ui/src/constants/teams.js is now a thin re-export, so every existing
// import keeps working unchanged.
//
// Copied by hand from Schedule Hub's (WFM Portal) Users page, 2026-09-30,
// because Schedule Hub's service API has no roster endpoint (only
// /schedule, /adherence and /labor; its /api/users needs a portal login, not
// the service key). This is a COPY, so it drifts: when someone joins, leaves
// or changes team in Schedule Hub, update the list here too. Replacing it
// with a live read is a Schedule Hub change (a service-key
// `GET /api/service/team-roster`), then a swap of this file's source only.
//
// Keyed on the agent's display name, because that is what the report's
// `agent` field carries (ai_suggestions.decided_by / conversations.closed_by
// are the Entra display name). Matching is case- and whitespace-insensitive.
// A name listed here that has no CXHub contacts yet simply never shows up on
// "AHT by account per rep" (no roster backstop there) -- the entry starts
// matching on its own once that person handles a contact. Scorecards is
// different: every name here (other than Management) is force-included as a
// zero-value row regardless of contacts (2026-10-08, Patrick's ask, after
// discovering the whole Nick Phone team and most of Adam Phone were simply
// invisible -- they have no `agent_permissions` row at all, since the Entra
// agent sync only ever populates from CXHub's own role security groups, and
// a phone-only rep who has never needed CXHub access was never added to
// either one). Schedule Hub's adherence lookup is keyed by EMAIL
// (schedule-hub/client.js's getAdherence), and this file's roster is names
// only, so a name with no `agent_permissions` row also has no email CXHub
// knows about by default -- closed by AGENT_TEAM_EMAILS below, a small,
// explicit fallback map Patrick supplied (2026-10-08) for exactly the
// Nick Phone / Adam Phone names this roster newly surfaced.
// Anyone in the report who is NOT listed here lands in "Unassigned", the
// same bucket Schedule Hub uses for a user with no team.
//
// Team names and order match Schedule Hub's own team chips; "Test Team" is
// left out (no real agents).
export const AGENT_TEAMS = [
  { team: 'Adam Phone', members: ['Chaquitta Turner', 'Charles Stratton', 'Christopher Walford', 'Danay Jimenez', 'Donna Thomas', 'Priya Messersmith', 'Sandra Hebert', 'Stacy Banes', 'Wandia Perry'] },
  { team: 'Nick Phone', members: ['Kayla Johnson', 'Lawrence Lopez', 'Tonya James'] },
  { team: 'Nick Email', members: ['Dasha Halstead', 'Emily McCutcheon', 'Jessica Watkins', 'Melody Cotter', 'Michael Leonard', 'Michelle Russell'] },
  { team: 'Patrick', members: ['Andra Malerbi', 'Jean Oeurnuch', 'Kemal Stewart', 'Nicholas Roberge'] },
  { team: 'Senior CSR', members: ['Ricale Aldrich', 'Timothy Krol'] },
  { team: 'Management', members: ['Adam Gragg', 'Gordon Matthews', 'Jennifer Hainsworth', 'Nick Boisseau', 'Patrick Lacroix'] },
]

export const UNASSIGNED_TEAM = 'Unassigned'

function normalizeName(name) {
  return String(name ?? '').trim().replace(/\s+/g, ' ').toLowerCase()
}

const TEAM_BY_NAME = new Map(
  AGENT_TEAMS.flatMap(({ team, members }) => members.map((m) => [normalizeName(m), team])),
)

export function teamOf(agentName) {
  return TEAM_BY_NAME.get(normalizeName(agentName)) ?? UNASSIGNED_TEAM
}

// The subset of `agents` (the report's own agent list, in its order) on
// `team`. Used to turn a team chip click into an agent selection.
export function agentsInTeam(agents, team) {
  return agents.filter((agent) => teamOf(agent) === team)
}

// The team chips to render for this report's agents: every team in
// AGENT_TEAMS order that has at least one agent in the data, then
// "Unassigned" last when anyone is unmatched. A team with nobody in the
// current window is hidden rather than shown as a chip that selects nothing.
export function teamsPresent(agents) {
  const present = new Set(agents.map(teamOf))
  const teams = AGENT_TEAMS.map(({ team }) => team).filter((team) => present.has(team))
  if (present.has(UNASSIGNED_TEAM)) teams.push(UNASSIGNED_TEAM)
  return teams
}

// Every member of every team EXCEPT Management -- the Scorecards roster
// backstop (2026-10-08): Management is left off deliberately (Patrick's
// call) since its members are supervisors whose email activity already
// shows up naturally when they touch a contact; there's no need to force a
// zero-row for them.
export function agentTeamsRosterExcludingManagement() {
  return AGENT_TEAMS.filter(({ team }) => team !== 'Management').flatMap(({ members }) => members)
}

// Non-agent accounts that show up on the Scorecards roster solely because
// they hold live CXHub admin access for IT/BSA/QA work, not because they
// work contacts (2026-10-16, Patrick's ask). The roster-widening backstop
// (admin-reporting.js's scorecards route) includes every entra_active
// agent_permissions row, and role alone can't tell these apart from a
// working agent who also happens to have admin rights (ROLE_PRIORITY
// treats both the same) -- so this is an explicit named exclusion, same
// pattern as the Management team above. Two of these five (Gordon
// Matthews, Jennifer Hainsworth) are already tagged Management and so
// already excluded from agentTeamsRosterExcludingManagement()'s own
// output; they're repeated here because the actual leak is from the
// agent_permissions-based half of the roster, not the team-roster half.
export const SCORECARDS_NON_AGENT_NAMES = [
  'Josh Jipson',
  'Gordon Matthews',
  'Jennifer Hainsworth',
  'Katie Lamy',
  'Taylor Acevedo',
]

const NON_AGENT_NAME_SET = new Set(SCORECARDS_NON_AGENT_NAMES.map(normalizeName))

export function isScorecardsNonAgent(name) {
  return NON_AGENT_NAME_SET.has(normalizeName(name))
}

// Email addresses for team-roster-only agents (no `agent_permissions` row,
// so no email CXHub otherwise knows), supplied by Patrick 2026-10-08
// specifically to resolve Schedule Hub adherence for them. Used ONLY by
// Scorecards' attachScorecardsAdherence as a fallback when an agent isn't
// already covered by a real agent_permissions row -- never used for auth,
// never creates or implies CXHub login access (see admin-reporting.js's own
// comment on this). Add an entry here as more team-roster-only agents need
// their ADH% resolved; an agent_permissions row's own email always wins if
// one later exists for the same name.
export const AGENT_TEAM_EMAILS = {
  'Kayla Johnson': 'kjohnson@1aauto.com',
  'Lawrence Lopez': 'llopez@1aauto.com',
  'Tonya James': 'tjames@1aauto.com',
  'Charles Stratton': 'cstratton@1aauto.com',
  'Christopher Walford': 'cwalford@1aauto.com',
  'Danay Jimenez': 'djimenez@1aauto.com',
  'Priya Messersmith': 'pmessersmith@1aauto.com',
  'Sandra Hebert': 'shebert@1aauto.com',
  'Stacy Banes': 'sbanes@1aauto.com',
  'Wandia Perry': 'wperry@1aauto.com',
}
